import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, Send } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useDirectMessages } from "@/hooks/useDirectMessages";
import { useAuth } from "@/context/AuthContext";
import { useDirectMessageContext } from "@/context/DirectMessageContext";

interface DirectMessagePopupProps {
  userId: string;
  userName: string;
  avatarUrl: string | null;
  style?: React.CSSProperties;
}

const MIN_W = 280;
const MIN_H = 300;
const MAX_W = 800;
const MAX_H = 900;
const DEFAULT_W = 480;
const DEFAULT_H = 600;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
};

const DirectMessagePopup: React.FC<DirectMessagePopupProps> = ({ userId, userName, avatarUrl, style }) => {
  const { user } = useAuth();
  const { closeChat, clearUnread } = useDirectMessageContext();
  const { messages, loading, sendMessage } = useDirectMessages(userId);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useIsMobile();

  // Drag state
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Resize state
  const [size, setSize] = useState({ width: DEFAULT_W, height: DEFAULT_H });
  const resizeRef = useRef<{ startX: number; startY: number; origW: number; origH: number } | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate initial position from style.right
  useEffect(() => {
    if (!position && style?.right && !isMobile) {
      const rightPx = typeof style.right === "string" ? parseInt(style.right) : (style.right as number);
      const x = window.innerWidth - rightPx - DEFAULT_W;
      const y = window.innerHeight - DEFAULT_H - 16;
      setPosition({ x, y });
    }
  }, [style, position, isMobile]);

  // Drag handlers (desktop only)
  const onDragStart = useCallback((e: React.MouseEvent) => {
    if (!position || isMobile) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: position.x, origY: position.y };
    setIsDragging(true);
  }, [position, isMobile]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - size.width, dragRef.current.origX + dx)),
        y: Math.max(0, Math.min(window.innerHeight - size.height, dragRef.current.origY + dy)),
      });
    };
    const onUp = () => {
      setIsDragging(false);
      dragRef.current = null;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
    };
  }, [isDragging, size.width, size.height]);

  // Resize handlers (desktop only)
  const onResizeStart = useCallback((e: React.MouseEvent) => {
    if (isMobile) return;
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { startX: e.clientX, startY: e.clientY, origW: size.width, origH: size.height };
    setIsResizing(true);
  }, [size, isMobile]);

  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e: MouseEvent) => {
      if (!resizeRef.current) return;
      const dw = e.clientX - resizeRef.current.startX;
      const dh = e.clientY - resizeRef.current.startY;
      setSize({
        width: Math.max(MIN_W, Math.min(MAX_W, resizeRef.current.origW + dw)),
        height: Math.max(MIN_H, Math.min(MAX_H, resizeRef.current.origH + dh)),
      });
    };
    const onUp = () => {
      setIsResizing(false);
      resizeRef.current = null;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
    };
  }, [isResizing]);

  useEffect(() => {
    clearUnread(userId);
  }, [userId, clearUnread]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;
    await sendMessage(input);
    setInput("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getInitials = (name: string) =>
    name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
  };

  // Mobile: fullscreen layout
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-background">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-3 bg-primary text-primary-foreground border-b">
          <div className="flex items-center gap-2 min-w-0">
            <Avatar className="h-8 w-8">
              {avatarUrl && <AvatarImage src={avatarUrl} alt={userName} />}
              <AvatarFallback className="text-xs bg-primary-foreground/20 text-primary-foreground">
                {getInitials(userName)}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm font-medium truncate">{userName}</span>
          </div>
          <button 
            onClick={() => closeChat(userId)} 
            className="p-2 hover:bg-primary-foreground/20 rounded min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {loading ? (
            <p className="text-xs text-muted-foreground text-center mt-4">Laden...</p>
          ) : messages.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center mt-4">Geen berichten nog. Stuur een bericht!</p>
          ) : (
            messages.map(msg => {
              const isMine = msg.sender_id === user?.id;
              return (
                <div key={msg.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                      isMine
                        ? "bg-primary text-primary-foreground rounded-br-sm"
                        : "bg-muted text-foreground rounded-bl-sm"
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words">{msg.message}</p>
                    <p className={`text-[10px] mt-0.5 ${isMine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                      {formatTime(msg.created_at)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input - fixed at bottom */}
        <div className="border-t p-2 flex gap-2 bg-background">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Typ een bericht..."
            rows={1}
            className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim()}
            className="p-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  // Desktop: existing floating popup
  return (
    <div
      ref={containerRef}
      className="fixed z-50 flex flex-col bg-popover border rounded-lg shadow-xl overflow-hidden"
      style={{
        left: position?.x ?? 0,
        top: position?.y ?? 0,
        width: size.width,
        height: size.height,
      }}
    >
      {/* Header - drag handle */}
      <div
        onMouseDown={onDragStart}
        className="flex items-center justify-between px-3 py-2 bg-primary text-primary-foreground border-b select-none"
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Avatar className="h-7 w-7">
            {avatarUrl && <AvatarImage src={avatarUrl} alt={userName} />}
            <AvatarFallback className="text-[10px] bg-primary-foreground/20 text-primary-foreground">
              {getInitials(userName)}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium truncate">{userName}</span>
        </div>
        <button onClick={() => closeChat(userId)} className="p-1 hover:bg-primary-foreground/20 rounded">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {loading ? (
          <p className="text-xs text-muted-foreground text-center mt-4">Laden...</p>
        ) : messages.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center mt-4">Geen berichten nog. Stuur een bericht!</p>
        ) : (
          messages.map(msg => {
            const isMine = msg.sender_id === user?.id;
            return (
              <div key={msg.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[75%] px-3 py-1.5 rounded-lg text-sm ${
                    isMine
                      ? "bg-primary text-primary-foreground rounded-br-sm"
                      : "bg-muted text-foreground rounded-bl-sm"
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">{msg.message}</p>
                  <p className={`text-[10px] mt-0.5 ${isMine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t p-2 flex gap-2">
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Typ een bericht..."
          rows={1}
          className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <button
          onClick={handleSend}
          disabled={!input.trim()}
          className="p-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>

      {/* Resize handle */}
      <div
        onMouseDown={onResizeStart}
        className="absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize"
        style={{ touchAction: "none" }}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" className="absolute bottom-0.5 right-0.5 text-muted-foreground/50">
          <path d="M9 1L1 9M9 5L5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  );
};

export default DirectMessagePopup;
